import React, { useRef, useState, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { useGSAP } from '@gsap/react';
import ThemeToggle from './ThemeToggle';
import SmallStepIndicator from './SmallStepIndicator';
import { useTheme } from 'hooks/useTheme';
import useMediaQuery from 'hooks/useMediaQuery';
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, ScrollSmoother);

const ACTIVE_OPACITY = 1;
const INACTIVE_OPACITY = 0.4;

const RangeCarousel = ({ rangeCarouselData }) => {
  const { isDarkMode } = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const isMobileSize = useMediaQuery();
  const clientContainerRef = useRef(null);
  const scrollTriggerInstance = useRef(null);
  const masterTl = useRef(null);
  const imagesRef = useRef([]);

  const formatNumber = (num) => (num >= 1 && num <= 9 ? `0${num}` : `${num}`);

  useLayoutEffect(() => {
    ScrollTrigger.normalizeScroll(true);
    return () => {
      ScrollTrigger.normalizeScroll(false);
    };
  }, []);

  useGSAP(
    () => {
      if (scrollTriggerInstance.current) {
        scrollTriggerInstance.current.kill();
      }
      if (masterTl.current) {
        masterTl.current.kill();
      }

      const items = rangeCarouselData.items;
      if (items.length === 0 || !clientContainerRef.current) return;

      const images = gsap.utils.toArray(
        '.range-image',
        clientContainerRef.current
      );
      const descriptions = gsap.utils.toArray(
        '.range-description',
        clientContainerRef.current
      );
      const stepItems = gsap.utils.toArray(
        '.range-step-item',
        clientContainerRef.current
      );

      imagesRef.current = images;

      gsap.set(images, {
        zIndex: 1,
        clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)',
      });
      if (images[0]) {
        gsap.set(images[0], {
          zIndex: 2,
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
        });
      }
      gsap.set(descriptions, { opacity: 0, pointerEvents: 'none' });
      if (descriptions[0]) {
        gsap.set(descriptions[0], { opacity: 1, pointerEvents: 'auto' });
      }
      stepItems.forEach((item, index) => {
        gsap.set(item, {
          opacity: index === 0 ? ACTIVE_OPACITY : INACTIVE_OPACITY,
        });
      });

      const scrollDistancePerItem = window.innerHeight;
      const totalScrollDuration = (items.length - 1) * scrollDistancePerItem;

      masterTl.current = gsap.timeline();

      for (let i = 1; i < items.length; i++) {
        const prevImage = images[i - 1];
        const currentImage = images[i];
        const prevDescription = descriptions[i - 1];
        const currentDescription = descriptions[i];
        const label = `item-start-${i}`;

        masterTl.current.addLabel(label, scrollDistancePerItem * (i - 1));

        masterTl.current
          .to(
            currentImage,
            {
              clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
              duration: scrollDistancePerItem,
              ease: 'none',
              force3D: true,
              onStart: () => gsap.set(currentImage, { zIndex: 2 }),
            },
            label
          )
          .to(
            prevImage,
            {
              clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
              duration: scrollDistancePerItem,
              ease: 'none',
              force3D: true,
              onComplete: () => gsap.set(prevImage, { zIndex: 1 }),
            },
            label
          );

        const descriptionFadeDuration = scrollDistancePerItem * 0.2;
        const imageMidpointOffset = scrollDistancePerItem * 0.5;

        masterTl.current.to(
          prevDescription,
          {
            opacity: 0,
            pointerEvents: 'none',
            duration: descriptionFadeDuration,
            ease: 'none',
          },
          `${label}+=${imageMidpointOffset - descriptionFadeDuration * 0.5}`
        );

        masterTl.current.to(
          currentDescription,
          {
            opacity: 1,
            pointerEvents: 'auto',
            duration: descriptionFadeDuration,
            ease: 'none',
          },
          `${label}+=${imageMidpointOffset - descriptionFadeDuration * 0.5}`
        );
      }

      scrollTriggerInstance.current = ScrollTrigger.create({
        trigger: clientContainerRef.current,
        start: 'top top',
        end: `+=${totalScrollDuration}`,
        pin: true,
        pinType: 'transform',
        anticipatePin: 1,
        animation: masterTl.current,
        scrub: 1,
        onUpdate: (self) => {
          const activeIndex = Math.round(self.progress * (items.length - 1));
          setCurrentStep(activeIndex + 1);
          stepItems.forEach((item, index) => {
            gsap.to(item, {
              opacity:
                index === activeIndex ? ACTIVE_OPACITY : INACTIVE_OPACITY,
              duration: 0.2,
              overwrite: 'auto',
            });
          });
        },
        invalidateOnRefresh: true,
      });
    },
    {
      scope: clientContainerRef,
      dependencies: [rangeCarouselData, isMobileSize],
    }
  );

  useLayoutEffect(() => {
    if (imagesRef.current.length === 0) return;

    imagesRef.current.forEach((img, i) => {
      const item = rangeCarouselData.items[i];
      if (img && item) {
        img.src = isDarkMode
          ? item.darkModeImageUrl || item.lightModeImageUrl
          : item.lightModeImageUrl;
      }
    });
  }, [isDarkMode, rangeCarouselData.items]);

  const handleStepClick = (targetStepIndex) => {
    const mainST = scrollTriggerInstance.current;
    const smoother = ScrollSmoother.get();
    if (!smoother || !mainST) return;

    const target0IndexedStep = targetStepIndex - 1;
    if (currentStep - 1 === target0IndexedStep) return;

    const scrollDistancePerItem = window.innerHeight;
    const targetScrollY =
      mainST.start + target0IndexedStep * scrollDistancePerItem;

    smoother.scrollTo(targetScrollY, {
      duration: 1.2,
      ease: 'power2.out',
    });
  };

  return (
    <div
      className={`slider-bg-second-colors range-crs-container page-wrapper full-pager ${
        isMobileSize ? 'range-crs-container-mobile' : ''
      }`}
      ref={clientContainerRef}
    >
      <div className='full-width full-height relative flex items-center justify-between range-crs-container-wrapper'>
        {!isMobileSize && (
          <div className='flex absolute bottom-0 items-center justify-between full-width'>
            <ThemeToggle isDarkVariant={true} />
            <SmallStepIndicator
              totalSteps={rangeCarouselData.items.length}
              currentStep={currentStep}
              setCurrentStep={handleStepClick}
            />
          </div>
        )}
        <div className='flex flex-column range-crs-steps'>
          {rangeCarouselData.items.map((item, index) => (
            <div
              key={item.title}
              className='range-step-item flex column-gap-24 items-center'
            >
              <span className='index-step font-secondary'>
                {formatNumber(index + 1)}
              </span>
              <span className='uppercase'>{item.title}</span>
            </div>
          ))}
        </div>
        {isMobileSize && (
          <div className='relative range-crs-desc-container'>
            {rangeCarouselData.items.map((item, index) => (
              <p
                key={item.description + index}
                className='range-description full-width absolute range-crs-desc'
                style={{ willChange: 'opacity' }}
              >
                {item.description}
              </p>
            ))}
          </div>
        )}
        <div className='absolute transform-x-50 range-crs-image-container'>
          {rangeCarouselData.items.map((item, index) => (
            <img
              key={item.title + index}
              src={
                isDarkMode
                  ? item.darkModeImageUrl || item.lightModeImageUrl
                  : item.lightModeImageUrl
              }
              alt={item.title}
              className='range-image full-height full-width absolute'
              style={{ willChange: 'clip-path' }}
            />
          ))}
        </div>
        {!isMobileSize && (
          <div className='relative range-crs-desc-container'>
            {rangeCarouselData.items.map((item, index) => (
              <p
                key={item.description + index}
                className='range-description center-abs full-width absolute range-crs-desc'
                style={{ willChange: 'opacity' }}
              >
                {item.description}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RangeCarousel;
